import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './AnalysisPage.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const RESULT_COLORS  = { H: '#1d4ed8', D: '#d97706', A: '#dc2626' };
const OUTCOMES       = ['H', 'D', 'A'];
const SURFACE_COLORS = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e' };
const LEVEL_COLORS   = { G: '#7c3aed', M: '#1d4ed8', F: '#0891b2', A: '#374151' };

// ── Baseball Analysis ─────────────────────────────────────────────────────

const RESULT_COLORS_BB = { home: '#1d4ed8', away: '#dc2626' };
const SEASON_COLORS    = ['#10b981', '#3b82f6', '#f97316', '#8b5cf6', '#f59e0b'];

function BaseballAnalysisContent({ season, onSeasonChange }) {
  const { t } = useTranslation();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const years = ['2024', '2025', '2026'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (season && season !== 'all') params.set('year', season);
      const res = await axios.get(`${API_URL}/baseball/analysis?${params}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [season]);

  useEffect(() => { load(); }, [load]);

  const hasData = data && data.calibration?.length > 0;

  return (
    <div className="analysis-section">
      <div className="section-header">
        <h2>{t('analysis.title')} — Baseball / MLB</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="season-selector">
            <button className={`season-pill${(!season || season === 'all') ? ' active' : ''}`}
              onClick={() => onSeasonChange('all')}>
              {t('common.all_seasons')}
            </button>
            {years.map(y => (
              <button key={y} className={`season-pill${season === y ? ' active' : ''}`}
                onClick={() => onSeasonChange(y)}>{y}</button>
            ))}
          </div>
          <button onClick={load} className="refresh-button">{t('common.refresh')}</button>
        </div>
      </div>

      {loading && <div className="empty-state"><p>{t('analysis.loading')}</p></div>}

      {!loading && !hasData && (
        <div className="empty-state">
          <p>{t('analysis.empty', { period: season && season !== 'all' ? season : t('common.all_seasons').toLowerCase() })}</p>
          <p className="subtitle">{t('analysis.empty_sub')}</p>
        </div>
      )}

      {!loading && hasData && (
        <>
          {/* ── CALIBRACIÓN ── */}
          <div className="analysis-block">
            <div className="analysis-block-header">
              <div>
                <div className="analysis-block-title">{t('analysis.calibration_title')}</div>
                <div className="analysis-subtitle">{t('analysis.calibration_subtitle')}</div>
              </div>
            </div>
            <div className="calibration-grid">
              {data.calibration.map(row => (
                <div key={row.range} className="calibration-card">
                  <div className="cal-range">{row.range}</div>
                  <div className="cal-accuracy" style={{
                    color: row.accuracy >= 0.65 ? '#10b981' : row.accuracy >= 0.55 ? '#d97706' : '#ef4444'
                  }}>
                    {(row.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="cal-label">{t('analysis.real_accuracy')}</div>
                  <div className="cal-bar-wrap">
                    <div className="cal-bar-fill" style={{ width: `${row.accuracy * 100}%` }} />
                    <div className="cal-bar-ref" style={{ left: `${row.avg_conf * 100}%` }}
                      title={`${t('common.confidence')}: ${(row.avg_conf * 100).toFixed(1)}%`} />
                  </div>
                  <div className="cal-total">{row.correct}/{row.total}</div>
                </div>
              ))}
            </div>
            <div className="analysis-note">{t('analysis.calibration_note')}</div>
          </div>

          {/* ── POR RESULTADO REAL (home / away) ── */}
          {data.by_result?.length > 0 && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('analysis.by_result_title')}</div>
              <div className="analysis-subtitle">{t('analysis.by_result_subtitle')}</div>
              <div className="by-result-grid">
                {data.by_result.map(r => (
                  <div key={r.result} className="by-result-card"
                    style={{ borderLeftColor: RESULT_COLORS_BB[r.result] || '#6b7280' }}>
                    <div className="by-result-header">
                      <span className="by-result-dot" style={{ background: RESULT_COLORS_BB[r.result] || '#6b7280' }} />
                      <span className="by-result-title">{r.result === 'home' ? 'Local' : 'Visitante'}</span>
                    </div>
                    <div className="by-result-accuracy" style={{
                      color: r.accuracy >= 0.65 ? '#10b981' : r.accuracy >= 0.5 ? '#d97706' : '#ef4444'
                    }}>
                      {(r.accuracy * 100).toFixed(1)}%
                    </div>
                    <div className="by-result-bar-wrap">
                      <div className="by-result-bar-fill"
                        style={{ width: `${r.accuracy * 100}%`, background: RESULT_COLORS_BB[r.result] || '#6b7280' }} />
                    </div>
                    <div className="by-result-detail">
                      {t('analysis.correct_of', { correct: r.correct, total: r.total })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── MATRIZ DE CONFUSIÓN 2×2 ── */}
          {data.confusion && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('analysis.confusion_title')}</div>
              <div className="analysis-subtitle">Predicción vs resultado real — béisbol no tiene empate</div>
              <div className="confusion-wrap">
                <table className="confusion-table">
                  <thead>
                    <tr>
                      <th className="conf-corner">{t('analysis.confusion_header')}</th>
                      <th style={{ color: RESULT_COLORS_BB.home }}>Local (real)</th>
                      <th style={{ color: RESULT_COLORS_BB.away }}>Visitante (real)</th>
                      <th className="conf-total-head">{t('analysis.confusion_total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        label: 'Local',   color: RESULT_COLORS_BB.home,
                        hh: data.confusion.home_home, ha: data.confusion.home_away,
                        total: data.confusion.home_home + data.confusion.home_away,
                      },
                      {
                        label: 'Visitante', color: RESULT_COLORS_BB.away,
                        hh: data.confusion.away_home, ha: data.confusion.away_away,
                        total: data.confusion.away_home + data.confusion.away_away,
                      },
                    ].map(row => {
                      const hhPct = row.total > 0 ? row.hh / row.total : 0;
                      const haPct = row.total > 0 ? row.ha / row.total : 0;
                      const isHome = row.label === 'Local';
                      return (
                        <tr key={row.label}>
                          <td className="conf-row-label" style={{ color: row.color }}>{row.label} (pred.)</td>
                          <td className={`conf-cell ${isHome ? 'diag' : ''}`}
                            style={{ background: isHome ? `rgba(16,185,129,${0.08 + hhPct * 0.55})` : `rgba(239,68,68,${hhPct * 0.35})` }}>
                            <span className="conf-count">{row.hh}</span>
                            <span className="conf-pct">{(hhPct * 100).toFixed(0)}%</span>
                          </td>
                          <td className={`conf-cell ${!isHome ? 'diag' : ''}`}
                            style={{ background: !isHome ? `rgba(16,185,129,${0.08 + haPct * 0.55})` : `rgba(239,68,68,${haPct * 0.35})` }}>
                            <span className="conf-count">{row.ha}</span>
                            <span className="conf-pct">{(haPct * 100).toFixed(0)}%</span>
                          </td>
                          <td className="conf-total">{row.total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="analysis-note">
                El modelo predice local en el {Math.round((data.confusion.home_home + data.confusion.home_away) / data.total * 100)}% de los casos. Cuando predice visitante, falla el {Math.round(data.confusion.away_home / (data.confusion.away_home + data.confusion.away_away) * 100)}% de las veces.
              </div>
            </div>
          )}

          {/* ── ACCURACY POR EQUIPO (TOP 15) ── */}
          {data.by_team?.length > 0 && (
            <div className="analysis-block">
              <div className="analysis-block-title">Equipos mejor predichos (top 15)</div>
              <div className="analysis-subtitle">Win% del modelo cuando predice a este equipo como ganador</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
                {data.by_team.map((team, i) => {
                  const pct = team.accuracy * 100;
                  const color = pct >= 62 ? '#10b981' : pct >= 57 ? '#d97706' : '#6b7280';
                  return (
                    <div key={team.team} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ width: '18px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', textAlign: 'right', flexShrink: 0 }}>
                        {i + 1}
                      </span>
                      <span style={{ width: '180px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {team.team}
                      </span>
                      <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.3s' }} />
                      </div>
                      <span style={{ width: '42px', fontSize: '0.8rem', fontWeight: 600, color, textAlign: 'right', flexShrink: 0 }}>
                        {pct.toFixed(1)}%
                      </span>
                      <span style={{ width: '48px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', textAlign: 'right', flexShrink: 0 }}>
                        {team.correct}/{team.total}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── POR TEMPORADA ── */}
          {(!season || season === 'all') && data.by_season?.length > 0 && (
            <div className="analysis-block">
              <div className="analysis-block-title">Accuracy por temporada</div>
              <div className="by-result-grid">
                {data.by_season.map((s, i) => (
                  <div key={s.season} className="by-result-card"
                    style={{ borderLeftColor: SEASON_COLORS[i % SEASON_COLORS.length] }}>
                    <div className="by-result-header">
                      <span className="by-result-dot" style={{ background: SEASON_COLORS[i % SEASON_COLORS.length] }} />
                      <span className="by-result-title">{s.season}</span>
                    </div>
                    <div className="by-result-accuracy" style={{
                      color: s.accuracy >= 0.58 ? '#10b981' : s.accuracy >= 0.53 ? '#d97706' : '#ef4444'
                    }}>
                      {(s.accuracy * 100).toFixed(1)}%
                    </div>
                    <div className="by-result-bar-wrap">
                      <div className="by-result-bar-fill"
                        style={{ width: `${s.accuracy * 100}%`, background: SEASON_COLORS[i % SEASON_COLORS.length] }} />
                    </div>
                    <div className="by-result-detail">
                      {t('analysis.correct_of', { correct: s.correct, total: s.total })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── PARTIDOS AJUSTADOS ── */}
          {data.tight_games && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('analysis.tight_title')}</div>
              <div className="analysis-subtitle">Partidos con confianza &lt; 55% — el modelo duda</div>
              <div className="tight-grid">
                <div className="tight-card">
                  <div className="tight-number">{data.tight_games.total}</div>
                  <div className="tight-label">{t('analysis.tight_matches')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#ef4444' }}>
                    {(data.tight_games.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="tight-label">{t('analysis.tight_accuracy')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#d97706' }}>
                    {data.tight_games.correct}
                  </div>
                  <div className="tight-label">correctas</div>
                </div>
              </div>
              <div className="analysis-note">
                {t('analysis.tight_note', { pct: (data.tight_games.accuracy * 100).toFixed(0) })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Tennis Analysis ───────────────────────────────────────────────────────

function TennisAnalysisContent({ season, onSeasonChange }) {
  const { t } = useTranslation();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const years = ['2024', '2025', '2026'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (season && season !== 'all') params.set('year', season);
      const res = await axios.get(`${API_URL}/tennis/analysis?${params}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [season]);

  useEffect(() => { load(); }, [load]);

  const hasData = data && data.calibration?.length > 0;

  return (
    <div className="analysis-section">
      <div className="section-header">
        <h2>{t('analysis.title')} — {t('leagues.Tennis')}</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="season-selector">
            <button className={`season-pill${(!season || season === 'all') ? ' active' : ''}`}
              onClick={() => onSeasonChange('all')}>
              {t('common.all_seasons')}
            </button>
            {years.map(y => (
              <button key={y} className={`season-pill${season === y ? ' active' : ''}`}
                onClick={() => onSeasonChange(y)}>{y}</button>
            ))}
          </div>
          <button onClick={load} className="refresh-button">{t('common.refresh')}</button>
        </div>
      </div>

      {loading && <div className="empty-state"><p>{t('analysis.loading')}</p></div>}

      {!loading && !hasData && (
        <div className="empty-state">
          <p>{t('analysis.empty', { period: season && season !== 'all' ? season : t('common.all_seasons').toLowerCase() })}</p>
          <p className="subtitle">{t('analysis.empty_sub')}</p>
        </div>
      )}

      {!loading && hasData && (
        <>
          {/* ── CALIBRACIÓN ── */}
          <div className="analysis-block">
            <div className="analysis-block-header">
              <div>
                <div className="analysis-block-title">{t('analysis.calibration_title')}</div>
                <div className="analysis-subtitle">{t('analysis.calibration_subtitle')}</div>
              </div>
            </div>
            <div className="calibration-grid">
              {data.calibration.map(row => (
                <div key={row.range} className="calibration-card">
                  <div className="cal-range">{row.range}</div>
                  <div className="cal-accuracy" style={{
                    color: row.accuracy >= 0.65 ? '#10b981' : row.accuracy >= 0.55 ? '#d97706' : '#ef4444'
                  }}>
                    {(row.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="cal-label">{t('analysis.real_accuracy')}</div>
                  <div className="cal-bar-wrap">
                    <div className="cal-bar-fill" style={{ width: `${row.accuracy * 100}%` }} />
                    <div className="cal-bar-ref" style={{ left: `${row.avg_conf * 100}%` }}
                      title={`${t('common.confidence')}: ${(row.avg_conf * 100).toFixed(1)}%`} />
                  </div>
                  <div className="cal-total">{row.correct}/{row.total}</div>
                </div>
              ))}
            </div>
            <div className="analysis-note">{t('analysis.calibration_note')}</div>
          </div>

          {/* ── ACCURACY POR SUPERFICIE ── */}
          {data.by_surface?.length > 0 && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('tennis.accuracy_title')}</div>
              <div className="by-result-grid">
                {data.by_surface.map(s => (
                  <div key={s.surface} className="by-result-card"
                    style={{ borderLeftColor: SURFACE_COLORS[s.surface] || '#6b7280' }}>
                    <div className="by-result-header">
                      <span className="by-result-dot" style={{ background: SURFACE_COLORS[s.surface] || '#6b7280' }} />
                      <span className="by-result-title">{t(`tennis.surface_${s.surface.toLowerCase()}`, { defaultValue: s.surface })}</span>
                    </div>
                    <div className="by-result-accuracy" style={{
                      color: s.accuracy >= 0.65 ? '#10b981' : s.accuracy >= 0.55 ? '#d97706' : '#ef4444'
                    }}>
                      {(s.accuracy * 100).toFixed(1)}%
                    </div>
                    <div className="by-result-bar-wrap">
                      <div className="by-result-bar-fill"
                        style={{ width: `${s.accuracy * 100}%`, background: SURFACE_COLORS[s.surface] || '#6b7280' }} />
                    </div>
                    <div className="by-result-detail">
                      {t('analysis.correct_of', { correct: s.correct, total: s.total })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ACCURACY POR NIVEL DE TORNEO ── */}
          {data.by_level?.length > 0 && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('tennis.accuracy_by_level')}</div>
              <div className="by-result-grid">
                {data.by_level.map(lv => (
                  <div key={lv.level} className="by-result-card"
                    style={{ borderLeftColor: LEVEL_COLORS[lv.level] || '#374151' }}>
                    <div className="by-result-header">
                      <span className="by-result-dot" style={{ background: LEVEL_COLORS[lv.level] || '#374151' }} />
                      <span className="by-result-title">{lv.level_name}</span>
                    </div>
                    <div className="by-result-accuracy" style={{
                      color: lv.accuracy >= 0.65 ? '#10b981' : lv.accuracy >= 0.55 ? '#d97706' : '#ef4444'
                    }}>
                      {(lv.accuracy * 100).toFixed(1)}%
                    </div>
                    <div className="by-result-bar-wrap">
                      <div className="by-result-bar-fill"
                        style={{ width: `${lv.accuracy * 100}%`, background: LEVEL_COLORS[lv.level] || '#374151' }} />
                    </div>
                    <div className="by-result-detail">
                      {t('analysis.correct_of', { correct: lv.correct, total: lv.total })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── PARTIDOS AJUSTADOS ── */}
          {data.tight_matches && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('analysis.tight_title')}</div>
              <div className="analysis-subtitle">{t('analysis.tight_subtitle')}</div>
              <div className="tight-grid">
                <div className="tight-card">
                  <div className="tight-number">{data.tight_matches.total}</div>
                  <div className="tight-label">{t('analysis.tight_matches')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#ef4444' }}>
                    {(data.tight_matches.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="tight-label">{t('analysis.tight_accuracy')}</div>
                </div>
              </div>
              <div className="analysis-note">
                {t('analysis.tight_note', { pct: (data.tight_matches.accuracy * 100).toFixed(0) })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Football Analysis ─────────────────────────────────────────────────────

export function AnalysisPage({ patterns, onRefresh, league, availableSeasons = [], season, onSeasonChange, isTennis = false, isBaseball = false }) {
  const { t } = useTranslation();

  const RESULT_LABELS = {
    H: t('results.H_short'),
    D: t('results.D_short'),
    A: t('results.A_short'),
  };

  if (isBaseball) {
    return <BaseballAnalysisContent season={season} onSeasonChange={onSeasonChange} />;
  }

  if (isTennis) {
    return <TennisAnalysisContent season={season} onSeasonChange={onSeasonChange} />;
  }

  if (!patterns) return (
    <div className="empty-state"><p>{t('analysis.loading')}</p></div>
  );

  const hasData = patterns.calibration?.length > 0;

  const confMatrix = {};
  OUTCOMES.forEach(p => {
    confMatrix[p] = {};
    OUTCOMES.forEach(a => { confMatrix[p][a] = 0; });
  });
  if (hasData) {
    patterns.confusion_matrix.forEach(({ predicted, actual, count }) => {
      if (confMatrix[predicted]) confMatrix[predicted][actual] = count;
    });
  }

  const rowTotals = {};
  OUTCOMES.forEach(p => {
    rowTotals[p] = OUTCOMES.reduce((s, a) => s + (confMatrix[p][a] || 0), 0);
  });

  const tightMatches = patterns.tight_matches || null;
  const closeCalls   = patterns.close_calls   || null;

  return (
    <div className="analysis-section">

      <div className="section-header">
        <h2>
          {t('analysis.title')}
          {league && (
            <span className="league-tag-header"> — {t(`leagues.${league}`, { defaultValue: league })}</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="season-selector">
            <button
              className={`season-pill${(!season || season === 'all') ? ' active' : ''}`}
              onClick={() => onSeasonChange('all')}
            >
              {t('common.all_seasons')}
            </button>
            {availableSeasons.map(s => (
              <button
                key={s}
                className={`season-pill${season === s ? ' active' : ''}`}
                onClick={() => onSeasonChange(s)}
              >
                {s}
              </button>
            ))}
          </div>
          <button onClick={onRefresh} className="refresh-button">{t('common.refresh')}</button>
        </div>
      </div>

      {!hasData ? (
        <div className="empty-state">
          <p>
            {t('analysis.empty', {
              period: season && season !== 'all' ? season : t('common.all_seasons').toLowerCase()
            })}
          </p>
          <p className="subtitle">{t('analysis.empty_sub')}</p>
        </div>
      ) : (
        <>
          {/* ── CALIBRACIÓN ── */}
          <div className="analysis-block">
            <div className="analysis-block-header">
              <div>
                <div className="analysis-block-title">{t('analysis.calibration_title')}</div>
                <div className="analysis-subtitle">{t('analysis.calibration_subtitle')}</div>
              </div>
            </div>
            <div className="calibration-grid">
              {patterns.calibration.map((row) => (
                <div key={row.range} className="calibration-card">
                  <div className="cal-range">{row.range}</div>
                  <div className="cal-accuracy" style={{
                    color: row.accuracy >= 0.6 ? '#10b981'
                         : row.accuracy >= 0.4 ? '#d97706'
                         : '#ef4444'
                  }}>
                    {(row.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="cal-label">{t('analysis.real_accuracy')}</div>
                  <div className="cal-bar-wrap">
                    <div className="cal-bar-fill" style={{ width: `${row.accuracy * 100}%` }} />
                    <div
                      className="cal-bar-ref"
                      style={{ left: `${row.avg_confidence * 100}%` }}
                      title={`${t('common.confidence')}: ${(row.avg_confidence * 100).toFixed(1)}%`}
                    />
                  </div>
                  <div className="cal-total">{row.correct}/{row.total}</div>
                </div>
              ))}
            </div>
            <div className="analysis-note">{t('analysis.calibration_note')}</div>
          </div>

          {/* ── MATRIZ DE CONFUSIÓN ── */}
          <div className="analysis-block">
            <div className="analysis-block-title">{t('analysis.confusion_title')}</div>
            <div className="analysis-subtitle">{t('analysis.confusion_subtitle')}</div>
            <div className="confusion-wrap">
              <table className="confusion-table">
                <thead>
                  <tr>
                    <th className="conf-corner">{t('analysis.confusion_header')}</th>
                    {OUTCOMES.map(a => (
                      <th key={a} style={{ color: RESULT_COLORS[a] }}>{RESULT_LABELS[a]}</th>
                    ))}
                    <th className="conf-total-head">{t('analysis.confusion_total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {OUTCOMES.map(p => (
                    <tr key={p}>
                      <td className="conf-row-label" style={{ color: RESULT_COLORS[p] }}>
                        {RESULT_LABELS[p]}
                      </td>
                      {OUTCOMES.map(a => {
                        const count  = confMatrix[p][a] || 0;
                        const pct    = rowTotals[p] > 0 ? count / rowTotals[p] : 0;
                        const isDiag = p === a;
                        return (
                          <td
                            key={a}
                            className={`conf-cell ${isDiag ? 'diag' : ''}`}
                            style={{
                              background: isDiag
                                ? `rgba(16,185,129,${0.08 + pct * 0.55})`
                                : `rgba(239,68,68,${pct * 0.35})`,
                            }}
                          >
                            <span className="conf-count">{count}</span>
                            <span className="conf-pct">{(pct * 100).toFixed(0)}%</span>
                          </td>
                        );
                      })}
                      <td className="conf-total">{rowTotals[p]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── ACCURACY POR RESULTADO REAL ── */}
          <div className="analysis-block">
            <div className="analysis-block-title">{t('analysis.by_result_title')}</div>
            <div className="analysis-subtitle">{t('analysis.by_result_subtitle')}</div>
            <div className="by-result-grid">
              {patterns.by_actual_result.map((row) => (
                <div key={row.result} className="by-result-card" style={{ borderLeftColor: RESULT_COLORS[row.result] }}>
                  <div className="by-result-header">
                    <span className="by-result-dot" style={{ background: RESULT_COLORS[row.result] }} />
                    <span className="by-result-title">{RESULT_LABELS[row.result] || row.result}</span>
                  </div>
                  <div className="by-result-accuracy" style={{
                    color: row.accuracy >= 0.6 ? '#10b981'
                         : row.accuracy >= 0.3 ? '#d97706'
                         : '#ef4444'
                  }}>
                    {(row.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="by-result-bar-wrap">
                    <div
                      className="by-result-bar-fill"
                      style={{ width: `${row.accuracy * 100}%`, background: RESULT_COLORS[row.result] }}
                    />
                  </div>
                  <div className="by-result-detail">
                    {t('analysis.correct_of', { correct: row.correct, total: row.total })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── PARTIDOS PAREJOS ── */}
          {tightMatches && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('analysis.tight_title')}</div>
              <div className="analysis-subtitle">{t('analysis.tight_subtitle')}</div>
              <div className="tight-grid">
                <div className="tight-card">
                  <div className="tight-number">{tightMatches.total}</div>
                  <div className="tight-label">{t('analysis.tight_matches')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#ef4444' }}>
                    {(tightMatches.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="tight-label">{t('analysis.tight_accuracy')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#d97706' }}>
                    {tightMatches.actual_draws}
                  </div>
                  <div className="tight-label">{t('analysis.tight_draws')}</div>
                </div>
              </div>
              <div className="analysis-note">
                {t('analysis.tight_note', { pct: (tightMatches.accuracy * 100).toFixed(0) })}
              </div>
            </div>
          )}

          {/* ── DECISIONES AJUSTADAS ── */}
          {closeCalls && (
            <div className="analysis-block">
              <div className="analysis-block-title">{t('analysis.close_title')}</div>
              <div className="analysis-subtitle">{t('analysis.close_subtitle')}</div>
              <div className="tight-grid">
                <div className="tight-card">
                  <div className="tight-number">{closeCalls.total}</div>
                  <div className="tight-label">{t('analysis.close_decisions')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#ef4444' }}>
                    {(closeCalls.accuracy * 100).toFixed(1)}%
                  </div>
                  <div className="tight-label">{t('analysis.close_accuracy')}</div>
                </div>
                <div className="tight-card">
                  <div className="tight-number" style={{ color: '#10b981' }}>
                    {closeCalls.correct}
                  </div>
                  <div className="tight-label">{t('analysis.close_correct', { total: closeCalls.total })}</div>
                </div>
              </div>
              <div className="analysis-note">{t('analysis.close_note')}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}